import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import JSZip from 'jszip';
import { getMimeType } from '../utils/mime-types.util';

import { FileContent, BlobUrlMap } from '../interfaces/file-system.interface';
import { SupplierData } from '../interfaces/supplier-data.interface';
import { TagReplacement } from '../interfaces/ai-content.interface';
import { COLOR_THEMES } from '../models/color-theme.model';
import { IndexedDBService } from './indexeddb.service';
import { QuoteConfigService } from './v2/quote-config.service';
import { TemplateVariableService } from './template-variable.service';
import { PhoneFormatterUtil } from '../utils/phone-formatter.util';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class WebsiteFileService {
  private memoryFiles = new Map<string, FileContent>();
  private blobUrls = new Map<string, string>();
  private originalCssFiles = new Map<string, string>(); // Store original CSS for deployment
  private updatedFiles = new Set<string>(); // Track files that have been updated
  private currentTemplate: number = 1;
  private currentTheme: string = 'default';
  private isBrowser: boolean;
  
  // Phase 1 Optimization: State tracking for smart asset loading
  private assetsLoadedForTemplate: number | null = null;
  private lastProcessedSupplierData: SupplierData | null = null;
  private lastProcessedTheme: string | null = null;

  constructor(
    private http: HttpClient,
    private indexedDBService: IndexedDBService,
    private quoteConfigService: QuoteConfigService,
    private templateVariableService: TemplateVariableService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  async initialize(templateNumber: number, forceReload: boolean = false): Promise<void> {
    this.currentTemplate = templateNumber;
    
    if (!this.isBrowser) {
      return;
    }

    // Clear existing data when initializing new template
    this.memoryFiles.clear();
    this.originalCssFiles.clear(); // Clear original CSS files cache
    
    // Cleanup old blob URLs
    for (const url of this.blobUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.blobUrls.clear();

    let loadFromAssets = forceReload;
    
    if (!forceReload) {
      // Try to load from IndexedDB first
      try {
        const cachedFiles = await this.indexedDBService.getFilesByTemplate(templateNumber);
        if (cachedFiles.size > 0) {
          console.log(`WebsiteFileService.initialize: Loaded ${cachedFiles.size} files from IndexedDB cache`);
          this.memoryFiles = cachedFiles;
        } else {
          console.log('WebsiteFileService.initialize: No cached files found, loading from assets');
          loadFromAssets = true;
        }
      } catch (error) {
        loadFromAssets = true;
      }
    }

    if (loadFromAssets) {
      await this.loadTemplateFromAssets(templateNumber);
      
      // Cache newly loaded files to IndexedDB
      await this.indexedDBService.storeFiles(this.memoryFiles, templateNumber);
    }

    // Generate blob URLs for all loaded files (cached or fresh)
    this.updateBlobUrls();

    // Update state tracking
    this.assetsLoadedForTemplate = templateNumber;
  }

  /**
   * Load template files from Angular assets
   */
  private async loadTemplateFromAssets(templateNumber: number): Promise<void> {
    const templatePath = `/assets/templates/supplier-template-${templateNumber}`;
    
    try {
      // Load HTML files
      await this.loadHtmlFiles(templatePath);
      
      // wait for 1 second
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Load CSS files  
      await this.loadCssFiles(templatePath);
      
      // Load JS files
      await this.loadJsFiles(templatePath);
      
      // Load image files
      await this.loadImageFiles(templatePath);
      
    } catch (error) {
      console.error('Failed to load template from assets:', error);
      throw error;
    }
  }

  private async loadHtmlFiles(templatePath: string): Promise<void> {
    const htmlFiles = ['index.html', 'about.html', 'capabilities.html', 'contact.html', 'quote.html', '404.html'];
    
    for (const file of htmlFiles) {
      try {
        const content = await firstValueFrom(
          this.http.get(`${templatePath}/${file}`, { responseType: 'text' })
        );
        
        // Replace template variables in HTML content
        const processedContent = this.templateVariableService.replaceTemplateVariables(content);
        
        this.memoryFiles.set(file, {
          path: file,
          content: processedContent,
          mimeType: 'text/html',
          lastModified: new Date()
        });
      } catch (error) {
        console.warn(`Failed to load HTML file ${file}:`, error);
      }
    }
  }

  private async loadCssFiles(templatePath: string): Promise<void> {
    const cssFiles = ['css/styles.css', 'css/color-schemes.css', 'css/quote-styles.css'];
    
    for (const file of cssFiles) {
      try {
        const content = await firstValueFrom(
          this.http.get(`${templatePath}/${file}`, { responseType: 'text' })
        );
        
        this.memoryFiles.set(file, {
          path: file,
          content: content,
          mimeType: 'text/css',
          lastModified: new Date()
        });
      } catch (error) {
        console.warn(`Failed to load CSS file ${file}:`, error);
      }
    }
  }

  private async loadJsFiles(templatePath: string): Promise<void> {
    // Extract template number from path
    const templateMatch = templatePath.match(/supplier-template-(\d+)/);
    const templateNumber = templateMatch ? parseInt(templateMatch[1]) : 1;
    
    // Template-specific JS files
    const jsFilesByTemplate: { [key: number]: string[] } = {
      1: [
        'js/script.js',    
        'js/quote-script.js',
        'js/content-manager.js',
        'js/dict.js'
      ],
      2: [
        'js/script.js',
        'js/quote-script.js',
        'js/content-manager.js',
        'js/dict.js'
      ],
      3: [
        'js/script.js',
        'js/quote-script.js',
        'js/content-manager.js',
        'js/dict.js'
      ],
      4: [
        'js/script.js',
        'js/quote-script.js',
        'js/content-manager.js',
        'js/dict.js'
      ],
      5: [
        'js/script.js',
        'js/quote-script.js',
        'js/content-manager.js',
        'js/dict.js'
      ]
    };
    
    const jsFiles = jsFilesByTemplate[templateNumber] || jsFilesByTemplate[1];
    
    for (const file of jsFiles) {
      try {
        let content = await firstValueFrom(
          this.http.get(`${templatePath}/${file}`, { responseType: 'text' })
        );
        
        // Inject configuration for quote-script.js
        if (file === 'js/quote-script.js') {
          const configScript = this.quoteConfigService.getQuoteScriptConfig();
          content = configScript + '\n' + content;
        }
        
        this.memoryFiles.set(file, {
          path: file,
          content: content,
          mimeType: 'application/javascript',
          lastModified: new Date()
        });
      } catch (error) {
        console.warn(`Failed to load JS file ${file}:`, error);
      }
    }
  }

  private async loadImageFiles(templatePath: string): Promise<void> {
    // Extract template number from path
    const templateMatch = templatePath.match(/supplier-template-(\d+)/);
    const templateNumber = templateMatch ? parseInt(templateMatch[1]) : 1;
    
    // Template-specific image files
    const imageFilesByTemplate: { [key: number]: string[] } = {
      1: [
        'images/about-placeholder-1.svg',
        'images/about-placeholder-2.svg',
        'images/hero-placeholder.svg' ,
        'images/equipment-placeholder.svg',
        'images/logo-placeholder.png'
      ],
      2: [
        'images/logo-placeholder.png',
        'images/hero-placeholder.svg',
        'images/about-placeholder-1.svg',
        'images/about-placeholder-2.svg',
        'images/equipment-placeholder.svg',
        'images/additional-placeholder.svg'
      ],
      3: [
        'images/logo-placeholder.png',
        'images/logo-placeholder-dark.svg',
        'images/hero-placeholder.svg',
        'images/about-placeholder.svg',
        'images/equipment-placeholder.svg'
      ],
      4: [
        'images/about-placeholder-icon.svg',
        'images/logo-placeholder-dark.svg',
        'images/about-placeholder-1.png',
        'images/about-placeholder-2.png',
        'images/about-placeholder-3.png',
        'images/equipment-placeholder.svg',
        'images/hero-placeholder.svg',
        'images/logo-placeholder.png',
      ],
      5: [
        'images/logo-placeholder-dark.svg',
        'images/logo-placeholder.png',
        'images/hero-placeholder.svg',
        'images/about-placeholder.svg',
        'images/about-placeholder-large.svg',
        'images/equipment-placeholder.svg'
      ]
    };
    
    const imageFiles = imageFilesByTemplate[templateNumber] || imageFilesByTemplate[1];
    
    for (const file of imageFiles) {
      try {
        const blob = await firstValueFrom(
          this.http.get(`${templatePath}/${file}`, { responseType: 'blob' })
        );
        
        this.memoryFiles.set(file, {
          path: file,
          content: blob,
          mimeType: blob.type || this.getMimeTypeForFile(file),
          lastModified: new Date()
        });
      } catch (error) {
        console.warn(`Failed to load image file ${file}:`, error);
      }
    }
  }

  /**
   * Phase 1 Optimization: Process only HTML content with supplier data and theme
   * This method reuses existing static assets (CSS, JS, Images) and only processes HTML files
   */
  private async processHtmlContent(supplierData: SupplierData, theme: string, appliedTagReplacements?: TagReplacement[], isEditMode?: string | null): Promise<void> {
    console.log('🔄 ProcessHtmlContent: Processing HTML files only with user data...', {
      companyName: supplierData.companyName,
      theme,
      fileCount: this.memoryFiles.size
    });

    if (!this.isBrowser) {
      console.log('⏭️ Skipping processHtmlContent - not in browser');
      return;
    }

    // Step 1: Load fresh HTML templates (only HTML files need user data replacement)
    console.log('🔄 Step 1: Loading fresh HTML templates...');
    const templatePath = `/assets/templates/supplier-template-${this.currentTemplate}`;
    await this.loadHtmlFiles(templatePath);
    console.log('✅ Step 1 Complete: Fresh HTML templates loaded');

    // Step 2: Process HTML files with supplier data
    console.log('🔄 Step 2: Processing HTML files with supplier data...');
    let htmlProcessed = 0;
    
    for (const [path, file] of this.memoryFiles) {
      if (file.mimeType === 'text/html' && typeof file.content === 'string') {
        this.processHtmlFile(file, supplierData, theme, appliedTagReplacements);
        htmlProcessed++;
      }
    }
    
    console.log(`✅ Step 2 Complete: Processed ${htmlProcessed} HTML files`);

    // Step 3: Update CSS files for theme changes (only if theme changed)
    if (theme !== this.lastProcessedTheme) {
      console.log('🔄 Step 3: Processing CSS files for theme change...');
      let cssProcessed = 0;
      
      for (const [path, file] of this.memoryFiles) {
        if (file.mimeType === 'text/css' && typeof file.content === 'string') {
          this.processCssFile(file, theme);
          cssProcessed++;
        }
      }
      
      console.log(`✅ Step 3 Complete: Processed ${cssProcessed} CSS files for theme`);
    } else {
      console.log('⏭️ Step 3 Skipped: Theme unchanged, reusing existing CSS');
    }

    // Step 4: Generate config files
    console.log('🔄 Step 4: Generating config files...');
    this.generateConfigFiles(supplierData, theme, isEditMode === 'true');
    console.log('✅ Step 4 Complete: Config files generated');

    // Step 5: Update blob URLs (only for changed files)
    console.log('🔄 Step 5: Updating blob URLs for changed files...');
    this.updateBlobUrls();
    console.log('✅ Step 5 Complete: Blob URLs updated');

    // Step 6: Sync to IndexedDB
    console.log('🔄 Step 6: Syncing to IndexedDB...');
    await this.syncToIndexedDB();
    console.log('✅ Step 6 Complete: Synced to IndexedDB');

    // Update state tracking
    this.lastProcessedSupplierData = { ...supplierData };
    this.lastProcessedTheme = theme;

    console.log('🎉 HTML content processing complete! Static assets reused for optimal performance.');
  }

  /**
   * Process template files with supplier data and theme
   * Phase 1 Optimization: Smart loading logic to avoid unnecessary asset reloading
   */
  async processFiles(supplierData: SupplierData, theme: string, appliedTagReplacements?: TagReplacement[], isEditMode?: string | null): Promise<void> {
    this.currentTheme = theme;
    
    console.log('🔄 Phase 1 Optimized: Starting smart file processing...', {
      companyName: supplierData.companyName,
      theme,
      currentTemplate: this.currentTemplate,
      assetsLoadedForTemplate: this.assetsLoadedForTemplate,
      fileCount: this.memoryFiles.size,
      isBrowser: this.isBrowser
    });
    
    if (!this.isBrowser) {
      console.log('⏭️ Skipping processFiles - not in browser');
      return;
    }

    // Phase 1 Optimization: Determine if we need full asset reload or just HTML processing
    const templateChanged = this.assetsLoadedForTemplate !== this.currentTemplate;
    const noAssetsLoaded = this.memoryFiles.size === 0;
    const needsFullReload = templateChanged || noAssetsLoaded;

    if (needsFullReload) {
      console.log('🔄 Full Asset Reload Required:', {
        reason: templateChanged ? 'Template changed' : 'No assets loaded',
        from: this.assetsLoadedForTemplate,
        to: this.currentTemplate
      });
      
      // Full reload: Initialize with all assets
      console.log('🔄 Step 1: Loading all template assets...');
      await this.initialize(this.currentTemplate, true);
      console.log('✅ Step 1 Complete: All assets loaded');
      
      // Process all files
      console.log('🔄 Step 2: Processing all files...');
      let htmlProcessed = 0;
      let cssProcessed = 0;
      
      for (const [path, file] of this.memoryFiles) {
        if (file.mimeType === 'text/html' && typeof file.content === 'string') {
          this.processHtmlFile(file, supplierData, theme, appliedTagReplacements);
          htmlProcessed++;
        } else if (file.mimeType === 'text/css' && typeof file.content === 'string') {
          this.processCssFile(file, theme);
          cssProcessed++;
        }
      }
      
      console.log(`✅ Step 2 Complete: Processed ${htmlProcessed} HTML and ${cssProcessed} CSS files`);
      
      // Generate config files
      console.log('🔄 Step 3: Generating config files...');
      this.generateConfigFiles(supplierData, theme, isEditMode === 'true');
      console.log('✅ Step 3 Complete: Config files generated');
      
      // Update blob URLs
      console.log('🔄 Step 4: Updating blob URLs...');
      this.updateBlobUrls();
      console.log(`✅ Step 4 Complete: Generated ${this.blobUrls.size} blob URLs`);
      
      // Sync to IndexedDB
      console.log('🔄 Step 5: Syncing to IndexedDB...');
      await this.syncToIndexedDB();
      console.log('✅ Step 5 Complete: Synced to IndexedDB');
      
      // Update state tracking
      this.lastProcessedSupplierData = { ...supplierData };
      this.lastProcessedTheme = theme;
      
      console.log('🎉 Full asset processing complete!');
      
    } else {
      console.log('⚡ Smart Optimization: Reusing static assets, processing HTML only');
      
      // Optimized path: Only process HTML content, reuse static assets
      await this.processHtmlContent(supplierData, theme, appliedTagReplacements, isEditMode);
      
      console.log('🎉 Optimized HTML processing complete! Static assets reused for maximum performance.');
    }
  }

  private processHtmlFile(file: FileContent, supplierData: SupplierData, theme: string, appliedTagReplacements?: TagReplacement[]): void {
    let content = file.content as string;
    const originalLength = content.length;
    
    console.log(`🔄 Processing ${file.path}...`);
    
    // Format address
    const addressParts = [];
    if (supplierData.streetAddress2) addressParts.push(supplierData.streetAddress2);
    if (supplierData.city) addressParts.push(supplierData.city);
    if (supplierData.postalCode) addressParts.push(supplierData.postalCode);
    if (supplierData.countryRegion) addressParts.push(supplierData.countryRegion);
    const address2 = addressParts.length > 0 ? ', ' + addressParts.join(', ') : '';

    // Apply replacements
    const replacements = {
      '{{CompanyName}}': supplierData.companyName || 'Your Company Name',
      '{{Email}}': supplierData.contactEmail || 'info@yourcompany.com',
      '{{Phone}}': PhoneFormatterUtil.formatPhoneNumber(supplierData.phoneNumber) || '(555) 123-4567',
      '{{Address_1}}': supplierData.streetAddress || '123 Main Street',
      '{{Address_2}}': address2,
      '{{LogoURL}}': '',
      '{{TemplateColor}}': theme, // This should be the theme name (e.g., "red", "green"), not color value
      // '{{About}}': supplierData.aboutContent || 'We are a leading company dedicated to providing exceptional services.',
      // '{{aboutContent}}': supplierData.aboutContent || 'We are a leading company dedicated to providing exceptional services.'
    };

    console.log(`  📋 Replacements for ${file.path}:`, replacements);
    
    let totalReplacements = 0;
    for (const [placeholder, value] of Object.entries(replacements)) {
      // Skip {{LogoURL}} replacement if there's a COMPANY_LOGO tag replacement to prevent conflicts
      if (placeholder === '{{LogoURL}}' && appliedTagReplacements) {
        const hasCompanyLogoReplacement = appliedTagReplacements.some(r => r.tagName === 'COMPANY_LOGO');
        if (hasCompanyLogoReplacement) {
          console.log(`    ⏭️ Skipping ${placeholder} replacement - COMPANY_LOGO tag replacement exists`);
          continue;
        }
      }
      
      const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      const matches = content.match(regex);
      if (matches && matches.length > 0) {
        content = content.replace(regex, value);
        totalReplacements += matches.length;
        console.log(`    ✅ Replaced ${matches.length}x ${placeholder} with "${value}"`);
      } else {
        console.log(`    ⏭️ No matches found for ${placeholder}`);
      }
    }
    
    // Special check for data-theme attribute
    if (content.includes('data-theme=')) {
      const dataThemeMatch = content.match(/data-theme="([^"]*)"/);
      if (dataThemeMatch) {
        console.log(`    🎨 HTML data-theme attribute set to: "${dataThemeMatch[1]}"`);
      }
    }
    
    console.log(`  📊 ${file.path} Summary:`, {
      totalReplacements,
      originalLength,
      newLength: content.length,
      companyNameFound: content.includes(supplierData.companyName || 'Your Company Name')
    });

    // Apply tag replacements from inline editor if provided
    if (appliedTagReplacements && appliedTagReplacements.length > 0) {
      console.log(`  🔄 Applying ${appliedTagReplacements.length} tag replacements to ${file.path}...`);
      
      for (const replacement of appliedTagReplacements) {
          if (replacement.tagName.startsWith('IMG_')) {
            continue;
          }
          
          const tagPattern = new RegExp(
            `(<[^>]*tag="${replacement.tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>)([^<]*)(</[^>]*>)`,
            'gi'
          );
          
          const matches = content.match(tagPattern);
          if (matches && matches.length > 0) {
            content = content.replace(tagPattern, (match, openTag, oldContent, closeTag) => {
              return `${openTag}${replacement.replacementHTML}${closeTag}`;
            });
            console.log(`    ✅ Applied replacement for tag "${replacement.tagName}"`);
          } else {
            console.log(`    ⏭️ No matches found for tag "${replacement.tagName}"`);
          }
      }
    }

    // Update file content
    file.content = content;
    file.lastModified = new Date();
  }

  private processCssFile(file: FileContent, theme: string): void {
    let content = file.content as string;
    
    // Handle CSS @import statements by inlining the imported content
    // This is necessary because blob URLs can't resolve relative @import paths
    const importMatches = content.match(/@import\s+['"](.*?)['"]\s*;/g);
    if (importMatches) {
      console.log(`Processing CSS imports in ${file.path}:`, importMatches);
      
      for (const importStatement of importMatches) {
        const importPath = importStatement.match(/@import\s+['"](.*?)['"]\s*;/)?.[1];
        if (importPath) {
          // Find the imported file in our loaded files
          const importedFile = Array.from(this.memoryFiles.values()).find((f: FileContent) => 
            f.path.endsWith(importPath) || f.path.endsWith(`css/${importPath}`)
          );
          
          if (importedFile) {
            console.log(`  ✅ Inlining ${importPath} content`);
            // Replace the @import statement with the actual content
            content = content.replace(importStatement, `/* Inlined from ${importPath} */\n${importedFile.content}\n/* End inline from ${importPath} */`);
          } else {
            console.warn(`  ❌ Could not find imported file: ${importPath}`);
          }
        }
      }
    }

    // Store original content for later blob URL processing
    file.content = content;
    file.lastModified = new Date();
    console.log(`CSS file ${file.path} processed successfully`);
  }

  private generateConfigFiles(supplierData: SupplierData, theme: string, editMode?: boolean): void {
    const sanitizedName = this.sanitizeFolderName(supplierData.companyName);
    this.generateSitemapXml(supplierData, sanitizedName);
    // Centralized check: Skip config generation if in edit mode
    if (editMode === true) {
      console.log('⏭️ Skipping config file generation - edit mode is active');
      const configFilesToRemove = ['data.json', 'config.yaml', 'config.release.yaml'];
      for (const configFile of configFilesToRemove) {
        if (this.memoryFiles.has(configFile)) {
          this.memoryFiles.delete(configFile);
          console.log(`🗑️ Removed existing config file: ${configFile}`);
        }
      }
      return;
    }
    
    const companyGuid = this.isBrowser ? (localStorage.getItem('company_guid') || this.generateGUID()) : this.generateGUID();
    
    const dataJson = {
      companyGuid: companyGuid,
      template: this.currentTemplate,
      colorTheme: theme,
      generatedAt: new Date().toISOString(),
      supplierData: supplierData
    };

    this.memoryFiles.set('data.json', {
      path: 'data.json',
      content: JSON.stringify(dataJson, null, 2),
      mimeType: 'application/json',
      lastModified: new Date()
    });

    // Generate config.yaml
    const configYaml = `hosts:
  - ${sanitizedName}${environment.hostSuffix}
  - www.${sanitizedName}${environment.hostSuffix}
primary_host: ${sanitizedName}${environment.hostSuffix}
company_guid: ${dataJson.companyGuid}`;

    if(environment.hostSuffix === '.ottodms.io') {
      this.memoryFiles.set('config.release.yaml', {
        path: 'config.release.yaml',
        content: configYaml,
        mimeType: 'application/x-yaml',
        lastModified: new Date()
      });
    }
    else {
      this.memoryFiles.set('config.yaml', {
        path: 'config.yaml',
        content: configYaml,
        mimeType: 'application/x-yaml',
        lastModified: new Date()
      });  
    }

    // Generate SEO files
    this.generateRobotsTxt(sanitizedName);
  }

  /**
   * Generate sitemap.xml for SEO optimization
   */
  private generateSitemapXml(supplierData: SupplierData, sanitizedName: string): void {
    const baseUrl = `https://${sanitizedName}${environment.hostSuffix}`;
    const currentDate = new Date().toISOString().split('T')[0];
    
    const sitemapContent = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url>
    <loc>${baseUrl}/</loc>
    <lastmod>${currentDate}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>1.0</priority>
  </url>
  <url>
    <loc>${baseUrl}/about.html</loc>
    <lastmod>${currentDate}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>
  <url>
    <loc>${baseUrl}/capabilities.html</loc>
    <lastmod>${currentDate}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>
  <url>
    <loc>${baseUrl}/contact.html</loc>
    <lastmod>${currentDate}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.7</priority>
  </url>
  <url>
    <loc>${baseUrl}/quote.html</loc>
    <lastmod>${currentDate}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.6</priority>
  </url>
</urlset>`;

    this.memoryFiles.set('sitemap.xml', {
      path: 'sitemap.xml',
      content: sitemapContent,
      mimeType: 'application/xml',
      lastModified: new Date()
    });

    console.log('✅ Generated sitemap.xml for SEO optimization');
    console.log(`📄 Sitemap content preview: ${sitemapContent.substring(0, 100)}...`);
  }

  /**
   * Generate robots.txt for SEO optimization
   */
  private generateRobotsTxt(sanitizedName: string): void {
    const baseUrl = `https://${sanitizedName}${environment.hostSuffix}`;
    
    const robotsContent = `User-agent: *
Allow: /

# Sitemap
Sitemap: ${baseUrl}/sitemap.xml

# Disallow admin or sensitive areas (if any)
# Disallow: /admin/
# Disallow: /private/


# Crawl delay (optional - helps prevent server overload)
Crawl-delay: 1`;

    this.memoryFiles.set('robots.txt', {
      path: 'robots.txt',
      content: robotsContent,
      mimeType: 'text/plain',
      lastModified: new Date()
    });

    console.log('✅ Generated robots.txt for SEO optimization');
    console.log(`📄 Robots.txt content preview: ${robotsContent.substring(0, 100)}...`);
  }

  /**
   * Generate blob URLs for all files
   * Phase 1 Optimization: Reuse existing blob URLs for unchanged static assets
   */
  private updateBlobUrls(): void {
    if (!this.isBrowser) return;
    
    // Keep track of old blob URLs for potential reuse
    const oldBlobUrls = new Map(this.blobUrls);
    const reusedUrls = new Set<string>();
    let newUrlsCreated = 0;
    let urlsReused = 0;

    // First pass: Generate blob URLs for all files EXCEPT CSS files
    for (const [path, file] of this.memoryFiles) {
      if (file.mimeType !== 'text/css') {
        // Phase 1 Optimization: For static assets (JS, images), try to reuse existing blob URLs
        const isStaticAsset = file.mimeType === 'application/javascript' || 
                             file.mimeType.startsWith('image/');
        const isUpdatedFile = this.updatedFiles.has(path);
        
        if (isStaticAsset && oldBlobUrls.has(path) && !isUpdatedFile) {
          // Reuse existing blob URL for unchanged static assets
          const existingUrl = oldBlobUrls.get(path)!;
          this.blobUrls.set(path, existingUrl);
          reusedUrls.add(existingUrl);
          urlsReused++;
          console.log(`  ♻️  Reused blob URL for static asset ${path}`);
        } else {
          // Create blob URL for changed, new, or updated files
          const blob = new Blob([file.content], { type: file.mimeType });
          const url = URL.createObjectURL(blob);
          this.blobUrls.set(path, url);
          reusedUrls.add(url);
          newUrlsCreated++;
          
          if (isUpdatedFile) {
            console.log(`  🔄 Updated blob content for ${path} (maintaining same path)`);
            // Remove from updated files set after processing
            this.updatedFiles.delete(path);
          } else {
            console.log(`  🆕 Created new blob URL for ${path}: ${url.substring(0, 50)}...`);
          }
        }
      }
    }

    // Second pass: Process CSS files to replace image URLs with blob URLs (for preview only)
    for (const [path, file] of this.memoryFiles) {
      if (file.mimeType === 'text/css' && typeof file.content === 'string') {
        // Get processed CSS content with blob URLs
        const processedCssContent = this.getProcessedCssContent(file);
        
        // Always create new blob URL for CSS (content may have changed due to theme/data)
        const blob = new Blob([processedCssContent], { type: file.mimeType });
        const url = URL.createObjectURL(blob);
        this.blobUrls.set(path, url);
        reusedUrls.add(url);
        newUrlsCreated++;
        
        console.log(`  🎨 Created preview blob URL for ${path}: ${url.substring(0, 50)}...`);
      }
    }

    // Clean up old blob URLs that are no longer needed
    // let urlsRevoked = 0;
    // for (const [path, url] of oldBlobUrls) {
    //   if (!reusedUrls.has(url)) {
    //     URL.revokeObjectURL(url);
    //     urlsRevoked++;
    //   }
    // }

    // console.log(`📊 Blob URL optimization: ${urlsReused} reused, ${newUrlsCreated} created, ${urlsRevoked} revoked`);
  }

  /**
   * Get processed CSS content with blob URLs (for preview only)
   * NOTE: This method returns modified CSS content for preview purposes only.
   * The original CSS content is preserved for deployment.
   */
  private getProcessedCssContent(file: FileContent): string {
    let content = file.content as string;
    
    // Store original CSS content for deployment (only if not already stored)
    if (!this.originalCssFiles.has(file.path)) {
      this.originalCssFiles.set(file.path, content);
    }

    // Handle CSS url() references for images
    const urlMatches = content.match(/url\(['"]?([^'")]+)['"]?\)/g);
    if (urlMatches) {
      for (const urlMatch of urlMatches) {
        const pathMatch = urlMatch.match(/url\(['"]?([^'")]+)['"]?\)/);
        if (pathMatch && pathMatch[1]) {
          let imagePath = pathMatch[1];
          
          // Skip if already a blob URL
          if (imagePath.startsWith('blob:')) {
            continue;
          }
          
          // Convert relative paths to absolute paths within the template
          if (imagePath.startsWith('../images/')) {
            imagePath = imagePath.replace('../images/', 'images/');
          } else if (imagePath.startsWith('./images/')) {
            imagePath = imagePath.replace('./images/', 'images/');
          } else if (imagePath.startsWith('images/')) {
            // Already correct format
          } else if (imagePath.startsWith('http') || imagePath.startsWith('data:')) {
            // Skip external URLs and data URLs
            continue;
          } else {
            // Skip other types of URLs
            continue;
          }
          
          // Get the blob URL for this image
          const blobUrl = this.blobUrls.get(imagePath);
          if (blobUrl) {
            // Replace the URL in CSS
            content = content.replace(urlMatch, `url('${blobUrl}')`);
          }
        }
      }
    }

    return content;
  }

  /**
   * Get blob URL for a specific file
   */
  getBlobUrl(path: string): string | null {
    return this.blobUrls.get(path) || null;
  }

  /**
   * Get file content
   */
  getFileContent(path: string): FileContent | null {
    return this.memoryFiles.get(path) || null;
  }

  /**
   * Package all files into a zip for deployment
   */
  async packageForDeployment(): Promise<Blob> {
    const zip = new JSZip();
    let seoFilesIncluded = 0;

    for (const [path, file] of this.memoryFiles) {
      let content = file.content;
      
      // Blur functionality disabled - no blur removal for deployment
      
      // For CSS files, use original content (without blob URLs)
      if (file.mimeType === 'text/css' && this.originalCssFiles.has(path)) {
        const originalCssContent = this.originalCssFiles.get(path)!;
        zip.file(path, originalCssContent);
        console.log(`📦 Packaged original CSS for deployment: ${path}`);
      } else {
        // For all other files, use processed content
        zip.file(path, content);
        
        // Log SEO files specifically
        if (path === 'sitemap.xml' || path === 'robots.txt') {
          console.log(`🔍 SEO file included in deployment: ${path}`);
          seoFilesIncluded++;
        }
      }
    }

    console.log(`📊 Deployment zip summary: ${this.memoryFiles.size} total files, ${seoFilesIncluded} SEO files included`);
    return await zip.generateAsync({ type: 'blob' });
  }

  /**
   * Remove blur effects from HTML content for deployment (disabled)
   */
  private removeBlurEffectsFromHtml(html: string): string {
    // Blur functionality disabled - return original HTML
    return html;
  }

  /**
   * Sync current files to IndexedDB
   */
  async syncToIndexedDB(): Promise<void> {
    if (!this.isBrowser) return;
    
    try {
      await this.indexedDBService.storeFiles(this.memoryFiles, this.currentTemplate);
      console.log('✅ Files synced to IndexedDB');
    } catch (error) {
      console.warn('Failed to sync to IndexedDB:', error);
    }
  }

  /**
   * Utility methods
   */

  private sanitizeFolderName(name: string): string {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-+|-+$/g, '') ;
  }

  private generateGUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  private getMimeTypeForFile(filePath: string): string {
    return getMimeType(filePath);
  }

  /**
   * Public getters
   */
  getCurrentTemplate(): number {
    return this.currentTemplate;
  }

  getCurrentTheme(): string {
    return this.currentTheme;
  }

  getFileCount(): number {
    return this.memoryFiles.size;
  }

  getAllFiles(): Map<string, FileContent> {
    return new Map(this.memoryFiles);
  }

  /**
   * Update content of an existing file
   */
  updateFileContent(path: string, content: string): void {
    const existingFile = this.memoryFiles.get(path);
    if (existingFile) {
      const updatedFile: FileContent = {
        ...existingFile,
        content: content,
        lastModified: new Date()
      };
      this.memoryFiles.set(path, updatedFile);
      this.updatedFiles.add(path); // Mark file as updated
      
      // Update blob URL if it exists
      if (this.blobUrls.has(path)) {
        // Revoke old blob URL
        const oldBlobUrl = this.blobUrls.get(path);
        if (oldBlobUrl) {
          URL.revokeObjectURL(oldBlobUrl);
        }
        
        // Create new blob URL for updated content
        const blob = new Blob([content], { type: updatedFile.mimeType });
        const newBlobUrl = URL.createObjectURL(blob);
        this.blobUrls.set(path, newBlobUrl);
      }
    } else {
      console.warn(`File ${path} not found for update`);
    }
  }

  /**
   * Update content of an existing file with blob content (for images)
   */
  updateFileWithBlob(path: string, blob: Blob): void {
    const existingFile = this.memoryFiles.get(path);
    if (existingFile) {
      // Update the file content in memory
      const updatedFile: FileContent = {
        ...existingFile,
        content: blob,
        mimeType: blob.type || existingFile.mimeType,
        lastModified: new Date()
      };
      this.memoryFiles.set(path, updatedFile);
      this.updatedFiles.add(path); // Mark file as updated
      
      // Don't update blob URLs here - let updateBlobUrls() handle it
      // This ensures consistent blob URL management
      console.log(`✅ Updated file ${path} content - will maintain same blob path`);
    } else {
      console.warn(`File ${path} not found for blob update`);
    }
  }

  /**
   * Force regeneration of blob URLs (call this after updating file content)
   */
  regenerateBlobUrls(): void {
    console.log('🔄 Regenerating blob URLs for updated content...');
    this.updateBlobUrls();
  }
} 